import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Spin, message, Space } from 'antd';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { firstSigningLikeUrl } from './contractFlowHelpers';
import { isLightreach } from './productsFinanceHubHelpers';
import DashboardModal from './DashboardModal';
import { SunlightIframeModal } from './SunlightIframeModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/TrinityProposalModal.css';
import './oneButton-css/ViewTrinityContractModal.css';
import './oneButton-css/SignFinancePartnerContractModal.css';

/** Subset of `AgreementRow` needed for sign flow (avoids circular imports from `AgreementsModal`). */
export type SignFinancePartnerContractModalRow = {
    key: string;
    quoteIdRaw: string;
    contractIdRaw: string;
    leadIdRaw: string;
    financePartnerRaw?: string;
    financePartner?: string;
    /** Trinity `Quote` — UUID sent as `quoteId` to `POST .../FinancePartner/contract` (Angular `data.Quote`). */
    quoteUuidRaw?: string;
    inPersonSigningUrlRaw?: string | null;
};

export type SignFinancePartnerContractModalProps = {
    open: boolean;
    row: SignFinancePartnerContractModalRow | null;
    opportunityId: string | number | null | undefined;
    opportunityDetails: unknown;
    onClose: () => void;
    onCompleted: () => void | Promise<void>;
    /**
     * Angular `create-sunnova-contract-renderer` `model.afterClose` (when nested signing iframe closes the outer modal):
     * `refreshData(true)` → `retrieveContract`, then `getLoanStatus()`, then `getChecklistStatus()` (`GetPageChanges`).
     * Parent runs that sequence; invoked after `onClose()` so `load()` is not skipped when row state clears.
     */
    onContractSigningIframeClosed: () => void;
    /** Passed from Agreements / hub; must align `DashboardModal` `theme` (defaults to dark if omitted). */
    theme?: 'dark' | 'light';
};

const IGS = 'IGS';

/** Sign modal `Spin` tip while `CheckContractSignedStatus` + loan refresh + parent reload run. */
const PARTNER_SIGN_REFRESH_SPIN_TIP = 'Updating signing status…';

function isIgsFinancePartnerRow(r: SignFinancePartnerContractModalRow): boolean {
    return String(r.financePartnerRaw || r.financePartner || '').trim().toUpperCase() === IGS;
}

/** Angular `CreateSunnovaContractComponent.ngOnInit`: `this.data.FinancePartner === LightreachPartnerName` (this row, not the opportunity). */
function isLightreachFinancePartnerRow(r: SignFinancePartnerContractModalRow | null): boolean {
    if (!r) return false;
    return isLightreach(String(r.financePartnerRaw || r.financePartner || ''));
}

/** Angular `getSunnovaContract` Lightreach fallback after `this.data.LeadId`. */
function readFinancePartnerSyncLeadId(details: unknown): string {
    const od = details as Record<string, unknown> | null | undefined;
    if (!od) return '';
    const nested = (od.OpportunityDetails ?? od.opportunityDetails) as Record<string, unknown> | undefined;
    return String(
        od.Finance_Partner_Sync_LeadID ??
            od.finance_Partner_Sync_LeadID ??
            nested?.Finance_Partner_Sync_LeadID ??
            nested?.Finance_Partner_Sync_LeadId ??
            '',
    ).trim();
}

/**
 * Angular `CreateSunnovaContractComponent`:
 * Lightreach when `data.FinancePartner === LightreachPartnerName`.
 * Lightreach sign: `leadId = data.LeadId || OpportunityDetails.Finance_Partner_Sync_LeadID`.
 */
export const SignFinancePartnerContractModal: React.FC<SignFinancePartnerContractModalProps> = ({
    open,
    row,
    opportunityId,
    opportunityDetails,
    onClose,
    onCompleted,
    onContractSigningIframeClosed,
    theme: themeProp = 'light',
}) => {
    /** Coerce so `rootClassName` is always `obd-theme-light` | `obd-theme-dark` (invalid strings break OneButtonUxRefresh sync-modal rules). */
    const modalTheme: 'dark' | 'light' = themeProp === 'dark' ? 'dark' : 'light';

    const lightReachPartner = isLightreachFinancePartnerRow(row);

    const [selectAction, setSelectAction] = useState<'embed' | 'email'>('embed');
    const [sales, setSales] = useState(false);
    const [self, setSelf] = useState(false);
    const [customer, setCustomer] = useState(false);
    const [loading, setLoading] = useState(false);
    /** Shown while `CheckContractSignedStatus` + loan status + parent `load()` run (after signing or modal dismiss). */
    const [postSignRefreshing, setPostSignRefreshing] = useState(false);
    const [checkResponse, setCheckResponse] = useState(false);
    const [sunnovaContractResult, setSunnovaContractResult] = useState<Record<string, unknown> | null>(null);
    const [lightReachContractMailSent, setLightReachContractMailSent] = useState(false);
    /** Trinity `CreateSunnovaContractComponent.openContractSigningFormIframeModal` — nested signing URL (Lightreach in-person), not `window.open`. */
    const [contractSigningIframeUrl, setContractSigningIframeUrl] = useState<string | null>(null);
    /**
     * Partner often fires both iframe `postMessage` (`close-ng-zorro-modals`) and modal dismiss — without this,
     * `onContractSigningIframeClosed` runs twice → duplicate `getLoanStatus` / `retrieveContract`.
     */
    const contractSigningIframeCloseHandledRef = useRef(false);

    /** Angular `create-sunnova-contract-renderer` — tracks successful sign/generate so main modal close can refresh. */
    const partnerFlowSucceededRef = useRef(false);

    const runAngularParityRefresh = useCallback(async () => {
        const cid = row?.contractIdRaw != null ? String(row.contractIdRaw).trim() : '';
        if (cid) {
            try {
                await quoteServices.checkContractSignedStatus(cid, true);
            } catch {
                /* non-fatal — still reload agreements */
            }
        }

        setPostSignRefreshing(true);
        try {
            /** `onCompleted` → parent `load()` already runs `contractGetLoanStatusByOpportunity` + `retrieveContract`. */
            await Promise.resolve(onCompleted());
        } finally {
            setPostSignRefreshing(false);
        }
    }, [row?.contractIdRaw, onCompleted]);

    useEffect(() => {
        if (contractSigningIframeUrl) contractSigningIframeCloseHandledRef.current = false;
    }, [contractSigningIframeUrl]);

    const closeContractSigningIframe = useCallback(() => {
        if (contractSigningIframeCloseHandledRef.current) return;
        contractSigningIframeCloseHandledRef.current = true;
        setContractSigningIframeUrl(null);
        partnerFlowSucceededRef.current = false;
        onClose();
        onContractSigningIframeClosed();
    }, [onClose, onContractSigningIframeClosed]);

    const handleModalClose = useCallback(() => {
        if (partnerFlowSucceededRef.current) {
            partnerFlowSucceededRef.current = false;
            void (async () => {
                await runAngularParityRefresh();
                onClose();
            })();
            return;
        }
        onClose();
    }, [onClose, runAngularParityRefresh]);

    /** Trinity `CreditCheckFormIframeModalComponent` — partner posts `{ type: 'close-ng-zorro-modals' }` to dismiss shells. */
    useEffect(() => {
        if (!contractSigningIframeUrl) return;
        const onMsg = (event: MessageEvent) => {
            if (event.data?.type === 'close-ng-zorro-modals') {
                closeContractSigningIframe();
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, [contractSigningIframeUrl, closeContractSigningIframe]);

    useEffect(() => {
        if (!open) setContractSigningIframeUrl(null);
    }, [open]);

    useEffect(() => {
        if (!open || !row) return;
        setSelectAction('embed');
        setSales(false);
        setSelf(false);
        setCustomer(false);
        setLoading(false);
        setPostSignRefreshing(false);
        setLightReachContractMailSent(false);
        partnerFlowSucceededRef.current = false;

        const igs = isIgsFinancePartnerRow(row);
        const url = String(row.inPersonSigningUrlRaw ?? '').trim();
        if (igs && url) {
            setSunnovaContractResult({
                quoteId: row.quoteUuidRaw ?? row.quoteIdRaw,
                Result: 1,
                file: url,
                leadId: row.leadIdRaw,
                message: '',
            });
            setCheckResponse(true);
        } else {
            setSunnovaContractResult(null);
            setCheckResponse(false);
        }
    }, [open, row?.key]);

    const generateDisabled = useMemo(() => {
        if (!row) return true;
        const emailNoRecipients =
            selectAction === 'email' && !sales && !self && !customer && !lightReachPartner;
        const lrMailDone = lightReachPartner && lightReachContractMailSent;
        return emailNoRecipients || lrMailDone || postSignRefreshing;
    }, [row, selectAction, sales, self, customer, lightReachPartner, lightReachContractMailSent, postSignRefreshing]);

    const generate = async () => {
        if (!row || opportunityId == null || String(opportunityId).trim() === '') {
            message.warning('Missing opportunity or row.');
            return;
        }
        const oid = opportunityId as string | number;

        if (lightReachPartner) {
            if (!row.quoteIdRaw?.trim()) {
                message.warning('QuoteId is not available');
                return;
            }
            const syncLead =
                String(row.leadIdRaw || '').trim() || readFinancePartnerSyncLeadId(opportunityDetails);
            setLoading(true);
            setCheckResponse(false);
            setSunnovaContractResult({});
            try {
                const res: any = await quoteServices.signFinancePartnerContract({
                    opportunityId: oid,
                    leadId: syncLead,
                    FinancePartnerQuoteId: row.quoteIdRaw,
                    OpportunityContractId: String(row.contractIdRaw ?? '').trim(),
                    isEmail: selectAction === 'email',
                });
                const parsedMsg = (() => {
                    try {
                        return JSON.parse(String(res?.Message ?? ''));
                    } catch {
                        return { message: res?.Message };
                    }
                })();
                if (res?.IsSuccessful) {
                    if (res.Data != null) {
                        partnerFlowSucceededRef.current = true;
                        if (selectAction !== 'email') {
                            const url = typeof res.Data === 'string' ? res.Data : firstSigningLikeUrl(res.Data);
                            if (url && /^https?:\/\//i.test(url)) {
                                setContractSigningIframeUrl(url);
                            }
                        }
                        setSunnovaContractResult({
                            message: res?.Data,
                            file: selectAction === 'email' ? firstSigningLikeUrl(res) : null,
                        });
                        setCheckResponse(true);
                        setLightReachContractMailSent(true);
                        setLoading(false);
                        await runAngularParityRefresh();
                    } else {
                        setSunnovaContractResult({
                            message: parsedMsg?.message || 'API returned null. Please try again.',
                        });
                        setCheckResponse(true);
                        setLightReachContractMailSent(false);
                    }
                } else {
                    setSunnovaContractResult({
                        message: parsedMsg?.message || 'Sign agreement was not successful.',
                    });
                    setCheckResponse(true);
                    setLightReachContractMailSent(false);
                    message.error(String(parsedMsg?.message || 'Sign agreement failed.'));
                }
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Sign agreement request failed.';
                message.error(msg);
                setSunnovaContractResult({ message: msg });
                setCheckResponse(true);
                setLightReachContractMailSent(false);
            } finally {
                setLoading(false);
            }
            return;
        }

        const quoteKey = String(row.quoteUuidRaw ?? '').trim();
        if (!quoteKey || quoteKey.includes('Quote')) {
            message.warning('QuoteId is not available');
            return;
        }

        setLoading(true);
        setCheckResponse(false);
        setSunnovaContractResult(null);
        try {
            const payload: {
                quoteId: string;
                opportunityId: string | number;
                action: string;
                salesRep?: boolean;
                loggedIn?: boolean;
                customer?: boolean;
            } = {
                quoteId: quoteKey,
                opportunityId: oid,
                action: selectAction,
                salesRep: false,
                loggedIn: false,
                customer: false,
            };
            if (selectAction === 'email') {
                payload.salesRep = !!sales;
                payload.loggedIn = !!self;
                payload.customer = !!customer;
            }
            const res: any = await quoteServices.createContractFinancePartner(payload);
            const file =
                (typeof res?.file === 'string' && res.file) ||
                (typeof res?.File === 'string' && res.File) ||
                firstSigningLikeUrl(res) ||
                null;
            if (res != null && file) {
                partnerFlowSucceededRef.current = true;
                setSunnovaContractResult({
                    quoteId: res?.quoteId ?? row.quoteUuidRaw,
                    leadId: res?.leadId ?? row.leadIdRaw,
                    file,
                    message: res?.message ?? res?.Message ?? '',
                });
                setCheckResponse(true);
                setLoading(false);
                await runAngularParityRefresh();
            } else {
                const errText =
                    typeof res?.message === 'string'
                        ? res.message
                        : typeof res?.Message === 'string'
                          ? res.Message
                          : 'Contract API did not return a document URL.';
                setSunnovaContractResult({ message: errText });
                setCheckResponse(true);
                message.warning(errText);
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Create finance partner contract failed.';
            message.error(msg);
            setSunnovaContractResult({ message: msg });
            setCheckResponse(true);
        } finally {
            setLoading(false);
        }
    };

    const fileUrl =
        sunnovaContractResult && typeof sunnovaContractResult.file === 'string'
            ? (sunnovaContractResult.file as string)
            : null;
    const resultMsg =
        sunnovaContractResult && sunnovaContractResult.message != null
            ? String(sunnovaContractResult.message)
            : '';

    const isError = !!(checkResponse && sunnovaContractResult && (
        lightReachPartner ? !lightReachContractMailSent : !sunnovaContractResult.file
    ));
    
    const showResults = !!(checkResponse && sunnovaContractResult && (
        !lightReachPartner ? true : (isError || selectAction === 'email')
    ));

    return (
        <>
        <DashboardModal
            title="Sign Finance Partner Contract"
            open={open && !!row}
            onClose={handleModalClose}
            width={620}
            footer={null}
            centered={false}
            zIndex={11000}
            getContainer={() => document.body}
            maskClosable={false}
            destroyOnClose
            theme={modalTheme}
            rootClassName={`obd-sync-modal-root obd-theme-${modalTheme} obd-sign-fp-modal-root`}
            className="obd-tpm-modal obd-vtc-modal obd-sign-fp-modal"
        >
            {row ? (
                <div className="obd-sign-fp-modal__body">
                    {loading || postSignRefreshing ? (
                        <div
                            className="obd-sign-fp-modal__loader"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <Spin size="default" className="obd-sign-fp-modal__loader-spin" />
                            <p className="obd-sign-fp-modal__loader-text">
                                {postSignRefreshing ? PARTNER_SIGN_REFRESH_SPIN_TIP : 'Generating…'}
                            </p>
                        </div>
                    ) : null}
                    <div className="obd-tpm">
                        <div className="obd-tpm__card obd-tpm__card--form">
                            <div
                                className="obd-tpm__grid"
                                style={{ gridTemplateColumns: 'minmax(160px, 1fr) auto', gap: '12px 16px' }}
                            >
                                <div className="obd-tpm__cell obd-tpm__cell--select">
                                    <OBDSelect
                                        className="obd-tpm__select"
                                        value={selectAction}
                                        onChange={(v) => setSelectAction(v as 'embed' | 'email')}
                                        popupMatchSelectWidth={false}
                                        options={[
                                            { value: 'embed', label: 'In-Person' },
                                            { value: 'email', label: 'Email' },
                                        ]}
                                    />
                                </div>
                                <div className="obd-tpm__cell obd-tpm__cell--action">
                                    {isError ? (
                                        <Space>
                                            <Button
                                                size="small"
                                                onClick={onClose}
                                                disabled={loading || postSignRefreshing}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="primary"
                                                size="small"
                                                className="obd-tpm__btn-primary"
                                                disabled={generateDisabled || loading || postSignRefreshing}
                                                onClick={() => void generate()}
                                            >
                                                Retry
                                            </Button>
                                        </Space>
                                    ) : (
                                        <Button
                                            type="primary"
                                            size="small"
                                            className="obd-tpm__btn-primary"
                                            disabled={generateDisabled || loading || postSignRefreshing}
                                            onClick={() => void generate()}
                                        >
                                            Generate Contract
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {selectAction === 'email' && !lightReachPartner ? (
                            <div className="obd-tpm__card" style={{ marginTop: 14 }}>
                                <div style={{ fontWeight: 600, marginBottom: 10 }}>Send To</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', alignItems: 'center' }}>
                                    <Checkbox checked={sales} onChange={(e) => setSales(e.target.checked)}>
                                        Sales Rep
                                    </Checkbox>
                                    <Checkbox checked={self} onChange={(e) => setSelf(e.target.checked)}>
                                        My Self
                                    </Checkbox>
                                    <Checkbox checked={customer} onChange={(e) => setCustomer(e.target.checked)}>
                                        Customer
                                    </Checkbox>
                                </div>
                            </div>
                        ) : null}

                        {showResults ? (
                            <div className="obd-tpm__results obd-tpm__results--split">
                                <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                                        {sunnovaContractResult.quoteId != null ? (
                                            <div>
                                                <strong>Quote Id</strong>{' '}
                                                {String(sunnovaContractResult.quoteId ?? 'null')}
                                            </div>
                                        ) : null}
                                        {sunnovaContractResult.leadId != null ? (
                                            <div style={{ marginTop: 6 }}>
                                                <strong>Lead Id</strong> {String(sunnovaContractResult.leadId ?? 'null')}
                                            </div>
                                        ) : null}
                                        {resultMsg ? (
                                            <div style={{ marginTop: 6, color: isError ? 'var(--ant-error-color, red)' : 'var(--ant-success-color, green)' }}>
                                                <strong>Message</strong> {resultMsg}
                                            </div>
                                        ) : null}
                                        {selectAction === 'email' && fileUrl ? (
                                            <div style={{ marginTop: 6 }}>
                                                <strong>File</strong>{' '}
                                                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                                    Click here
                                                </a>
                                            </div>
                                        ) : null}
                                        {selectAction === 'embed' && fileUrl ? (
                                            <div style={{ marginTop: 12, textAlign: 'center' }}>
                                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="Inperson-link">
                                                    Click here for Partner Contract
                                                </a>
                                            </div>
                                        ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </DashboardModal>
        <SunlightIframeModal
            open={!!contractSigningIframeUrl}
            title="Contract Signing Form"
            url={contractSigningIframeUrl}
            onClose={closeContractSigningIframe}
            width="90%"
            iframeHeight="min(calc(85vh - 160px), 720px)"
            centered
            theme={modalTheme}
            zIndex={11020}
            loadingText="Loading signing form…"
        />
        </>
    );
};

export default SignFinancePartnerContractModal;
